// Édition d'une condition (affichage d'un champ ou d'une section, pièce conditionnelle, règle
// d'éligibilité) : toujours, une comparaison, ou plusieurs comparaisons combinées par ET ou OU.
// La valeur comparée est saisie selon le type du champ visé (oui ou non, option, nombre, texte).
import { OPERATEURS_CONDITION, type Champ, type Condition, type OperateurCondition } from '@cc/contrats';
import { Bouton, Champ as ChampTexte, Selection } from '@cc/ui';
import { Plus, Trash2 } from 'lucide-react';
import demarches from '../demarches.module.css';
import styles from './constructeur.module.css';

export const LIBELLES_OPERATEURS: Record<OperateurCondition, string> = {
  egal: 'est égal à',
  different: 'est différent de',
  superieur: 'est supérieur à',
  superieur_ou_egal: 'est supérieur ou égal à',
  inferieur: 'est inférieur à',
  inferieur_ou_egal: 'est inférieur ou égal à',
  contient: 'contient',
  renseigne: 'est renseigné',
  non_renseigne: 'n\'est pas renseigné',
};

type Simple = { champ: string; operateur: OperateurCondition; valeur?: string | number | boolean | null };

const SANS_VALEUR: OperateurCondition[] = ['renseigne', 'non_renseigne'];

function simples(c: Condition | undefined): { mode: 'toujours' | 'une' | 'et' | 'ou'; liste: Simple[] } {
  if (!c) return { mode: 'toujours', liste: [] };
  if ('et' in c) return { mode: 'et', liste: c.et.filter((x): x is Simple => 'champ' in x) };
  if ('ou' in c) return { mode: 'ou', liste: c.ou.filter((x): x is Simple => 'champ' in x) };
  return { mode: 'une', liste: [c] };
}

function assembler(mode: 'toujours' | 'une' | 'et' | 'ou', liste: Simple[]): Condition | undefined {
  if (mode === 'toujours' || liste.length === 0) return undefined;
  if (mode === 'une') return liste[0];
  return mode === 'et' ? { et: liste } : { ou: liste };
}

/** Texte lisible d'une condition, pour les listes et les aperçus. */
export function texteCondition(c: Condition | undefined, champs: Champ[]): string {
  if (!c) return 'toujours';
  const nom = (code: string) => champs.find((x) => x.code === code)?.libelle ?? code;
  if ('et' in c) return c.et.map((x) => texteCondition(x, champs)).join(' et ');
  if ('ou' in c) return c.ou.map((x) => texteCondition(x, champs)).join(' ou ');
  const cible = champs.find((x) => x.code === c.champ);
  let valeur = '';
  if (!SANS_VALEUR.includes(c.operateur)) {
    if (typeof c.valeur === 'boolean') valeur = c.valeur ? 'oui' : 'non';
    else valeur = cible?.options?.find((o) => o.valeur === c.valeur)?.libelle ?? String(c.valeur ?? '');
  }
  return `« ${nom(c.champ)} » ${LIBELLES_OPERATEURS[c.operateur]}${valeur ? ` « ${valeur} »` : ''}`;
}

export interface ProprietesEditeurCondition {
  condition: Condition | undefined;
  onChanger: (c: Condition | undefined) => void;
  /** Champs sur lesquels la condition peut porter (le champ édité lui-même est exclu par l'appelant). */
  champs: Champ[];
  libelle?: string;
  id?: string;
}

function ValeurComparee({ cible, simple, onChanger }: { cible: Champ | undefined; simple: Simple; onChanger: (v: Simple['valeur']) => void }) {
  if (SANS_VALEUR.includes(simple.operateur)) return null;
  if (cible?.type === 'oui_non') {
    return <Selection libelle="Valeur" value={simple.valeur === true ? 'oui' : simple.valeur === false ? 'non' : ''} optionVide="Choisir" options={[{ valeur: 'oui', libelle: 'Oui' }, { valeur: 'non', libelle: 'Non' }]} onChange={(e) => onChanger(e.target.value === '' ? null : e.target.value === 'oui')} />;
  }
  if (cible?.options && cible.options.length > 0) {
    return <Selection libelle="Valeur" value={typeof simple.valeur === 'string' ? simple.valeur : ''} optionVide="Choisir une option" options={cible.options.map((o) => ({ valeur: o.valeur, libelle: o.libelle }))} onChange={(e) => onChanger(e.target.value || null)} />;
  }
  if (cible?.type === 'nombre' || cible?.type === 'montant') {
    return <ChampTexte libelle="Valeur" type="number" step="any" value={typeof simple.valeur === 'number' ? String(simple.valeur) : ''} onChange={(e) => onChanger(e.target.value === '' ? null : Number(e.target.value))} />;
  }
  return <ChampTexte libelle="Valeur" value={typeof simple.valeur === 'string' ? simple.valeur : simple.valeur === null || simple.valeur === undefined ? '' : String(simple.valeur)} onChange={(e) => onChanger(e.target.value)} />;
}

export function EditeurCondition({ condition, onChanger, champs, libelle = 'Condition d\'affichage', id }: ProprietesEditeurCondition) {
  const { mode, liste } = simples(condition);
  const modifierListe = (suivante: Simple[], m = mode) => onChanger(assembler(m === 'toujours' ? 'une' : m, suivante));
  const premier = champs[0];
  const nouvelle = (): Simple => ({ champ: premier?.code ?? '', operateur: 'renseigne' });
  return (
    <fieldset className={demarches.sousEditeur}>
      <legend>{libelle}</legend>
      <Selection
        id={id}
        libelle="Quand"
        value={mode}
        options={[
          { valeur: 'toujours', libelle: 'Toujours' },
          { valeur: 'une', libelle: 'Si une condition est remplie' },
          { valeur: 'et', libelle: 'Si toutes ces conditions sont remplies (et)' },
          { valeur: 'ou', libelle: 'Si l\'une de ces conditions est remplie (ou)' },
        ]}
        disabled={champs.length === 0}
        aide={champs.length === 0 ? 'Ajoutez d\'abord un autre champ au formulaire.' : undefined}
        onChange={(e) => {
          const m = e.target.value as 'toujours' | 'une' | 'et' | 'ou';
          if (m === 'toujours') onChanger(undefined);
          else onChanger(assembler(m, m === 'une' ? [liste[0] ?? nouvelle()] : liste.length > 0 ? liste : [nouvelle()]));
        }}
      />
      {mode !== 'toujours'
        ? liste.map((s, i) => {
            const cible = champs.find((x) => x.code === s.champ);
            return (
              <div key={i} className={styles.ligneCondition}>
                <Selection libelle="Champ" value={s.champ} options={champs.map((c) => ({ valeur: c.code, libelle: c.libelle }))} onChange={(e) => modifierListe(liste.map((x, j) => (j === i ? { champ: e.target.value, operateur: x.operateur } : x)))} />
                <Selection libelle="Opérateur" value={s.operateur} options={OPERATEURS_CONDITION.map((o) => ({ valeur: o, libelle: LIBELLES_OPERATEURS[o] }))} onChange={(e) => modifierListe(liste.map((x, j) => (j === i ? { ...x, operateur: e.target.value as OperateurCondition, valeur: SANS_VALEUR.includes(e.target.value as OperateurCondition) ? undefined : x.valeur } : x)))} />
                <ValeurComparee cible={cible} simple={s} onChanger={(v) => modifierListe(liste.map((x, j) => (j === i ? { ...x, valeur: v } : x)))} />
                {mode !== 'une' ? (
                  <Bouton petit variante="discret" icone={Trash2} aria-label={`Retirer la condition ${i + 1}`} onClick={() => (liste.length === 1 ? onChanger(undefined) : modifierListe(liste.filter((_, j) => j !== i)))}>
                    Retirer
                  </Bouton>
                ) : null}
              </div>
            );
          })
        : null}
      {mode === 'et' || mode === 'ou' ? (
        <div>
          <Bouton petit variante="secondaire" icone={Plus} onClick={() => modifierListe([...liste, nouvelle()])}>
            Ajouter une condition
          </Bouton>
        </div>
      ) : null}
    </fieldset>
  );
}
